/**
 * AttributeSourceSummary
 *
 * Displays product attributes grouped by their origin source (feed, AI extracted, AI researched, manual).
 * Fetches from /api/product-enrichment/:productId/attributes-full endpoint.
 *
 * Used in Content Review panel to show attribute provenance.
 *
 * Date: January 14, 2026
 */

import React, { useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  Database,
  Cpu,
  Search,
  User,
  ChevronDown,
  ChevronRight,
  HelpCircle,
} from 'lucide-react';
import { authFetch } from '@/utils/auth-fetch';
import { format } from 'date-fns';

interface Attribute {
  name: string;
  value: unknown;
  unit: string | null;
  origin: 'feed' | 'ai_extracted' | 'ai_researched' | 'manual' | 'unknown';
  confidence: number;
  sourceText: string | null;
  sourceUrl: string | null;
  lastModified: string | null;
  modifiedBy: string | null;
  researchedVia?: string | null;
}

interface AttributesSummary {
  feed: number;
  aiExtracted: number;
  aiResearched: number;
  manual: number;
  unknown: number;
  total: number;
}

interface AttributeSourceSummaryProps {
  productId: string;
  defaultOpen?: boolean;
}

const originConfig = {
  feed: {
    label: 'Feed Original',
    icon: Database,
    variant: 'outline' as const,
    color: 'text-blue-500',
  },
  ai_extracted: {
    label: 'AI Extracted',
    icon: Cpu,
    variant: 'secondary' as const,
    color: 'text-purple-500',
  },
  ai_researched: {
    label: 'AI Researched',
    icon: Search,
    variant: 'default' as const,
    color: 'text-green-500',
  },
  manual: {
    label: 'Manual Entry',
    icon: User,
    variant: 'outline' as const,
    color: 'text-orange-500',
  },
  unknown: {
    label: 'Unknown',
    icon: HelpCircle,
    variant: 'outline' as const,
    color: 'text-gray-400',
  },
};

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return '-';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function AttributeGroup({
  origin,
  attributes,
  defaultOpen = false,
}: {
  origin: keyof typeof originConfig;
  attributes: Attribute[];
  defaultOpen?: boolean;
}) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const config = originConfig[origin];
  const Icon = config.icon;

  if (attributes.length === 0) return null;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="flex items-center justify-between w-full py-2 px-3 hover:bg-muted/50 rounded-md transition-colors">
        <div className="flex items-center gap-2">
          <Icon className={`h-4 w-4 ${config.color}`} />
          <span className="font-medium text-sm">{config.label}</span>
          <Badge variant={config.variant} className="ml-2">
            {attributes.length}
          </Badge>
        </div>
        {isOpen ? (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        )}
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="pl-6 pr-2 py-2 space-y-2">
          {attributes.map((attr) => (
            <div
              key={attr.name}
              className="flex flex-col gap-1 py-2 border-b border-dashed last:border-0"
            >
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-muted-foreground">
                  {attr.name.replace(/_/g, ' ')}
                </span>
                {attr.confidence < 1 && (
                  <Badge variant="outline" className="text-xs">
                    {Math.round(attr.confidence * 100)}% conf.
                  </Badge>
                )}
              </div>
              <div className="text-sm">
                {formatValue(attr.value)}
                {attr.unit && !String(attr.value).toLowerCase().endsWith(attr.unit.toLowerCase()) && (
                  <span className="text-muted-foreground ml-1">{attr.unit}</span>
                )}
              </div>
              {(attr.sourceText || attr.sourceUrl) && (
                <div className="text-xs text-muted-foreground flex items-center gap-1">
                  <span className="text-gray-400">Source:</span>
                  {attr.sourceUrl ? (
                    <a
                      href={attr.sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-500 hover:text-blue-700 hover:underline truncate max-w-[250px] inline-flex items-center gap-1"
                      title={attr.sourceUrl}
                    >
                      {attr.sourceUrl.length > 40 ? attr.sourceUrl.substring(0, 40) + '...' : attr.sourceUrl}
                      <Search className="h-3 w-3 flex-shrink-0" />
                    </a>
                  ) : (
                    <span className="italic truncate max-w-[250px]" title={attr.sourceText || ''}>
                      {attr.sourceText}
                    </span>
                  )}
                </div>
              )}
              {attr.lastModified && (
                <div className="text-xs text-muted-foreground">
                  Updated: {format(new Date(attr.lastModified), 'MMM d, yyyy HH:mm')}
                </div>
              )}
            </div>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

export function AttributeSourceSummary({
  productId,
  defaultOpen = false,
}: AttributeSourceSummaryProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [attributes, setAttributes] = useState<Attribute[]>([]);
  const [summary, setSummary] = useState<AttributesSummary | null>(null);

  useEffect(() => {
    if (!productId || !isOpen) return;

    const fetchAttributes = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await authFetch(
          `/api/product-enrichment/${productId}/attributes-full`
        );

        if (!response.ok) {
          throw new Error('Failed to fetch attributes');
        }

        const data = await response.json();

        if (data.success) {
          setAttributes(data.data.attributes || []);
          setSummary(data.data.summary || null);
        } else {
          throw new Error(data.error || 'Unknown error');
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load attributes');
      } finally {
        setLoading(false);
      }
    };

    fetchAttributes();
  }, [productId, isOpen]);

  // Group attributes by origin
  const groupedAttributes = attributes.reduce(
    (acc, attr) => {
      const origin = attr.origin || 'unknown';
      if (!acc[origin]) acc[origin] = [];
      acc[origin].push(attr);
      return acc;
    },
    {} as Record<string, Attribute[]>
  );

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="flex items-center gap-2 w-full py-2 px-3 hover:bg-muted/50 rounded-md transition-colors border">
        <Database className="h-4 w-4 text-muted-foreground" />
        <span className="font-medium text-sm">Source Data</span>
        {summary && (
          <Badge variant="secondary" className="ml-2">
            {summary.total} attributes
          </Badge>
        )}
        <div className="ml-auto">
          {isOpen ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="mt-2 border rounded-md p-3 space-y-1">
          {loading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-2">
                  <Skeleton className="h-4 w-4" />
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-5 w-8 ml-auto" />
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="text-sm text-red-500">{error}</div>
          ) : attributes.length === 0 ? (
            <div className="text-sm text-muted-foreground">
              No attributes found for this product.
            </div>
          ) : (
            <>
              {/* Summary badges */}
              {summary && (
                <div className="flex flex-wrap gap-2 pb-3 mb-2 border-b">
                  {summary.feed > 0 && (
                    <Badge variant="outline" className="text-xs">
                      <Database className="h-3 w-3 mr-1 text-blue-500" />
                      Feed: {summary.feed}
                    </Badge>
                  )}
                  {summary.aiExtracted > 0 && (
                    <Badge variant="secondary" className="text-xs">
                      <Cpu className="h-3 w-3 mr-1 text-purple-500" />
                      AI Extracted: {summary.aiExtracted}
                    </Badge>
                  )}
                  {summary.aiResearched > 0 && (
                    <Badge variant="default" className="text-xs">
                      <Search className="h-3 w-3 mr-1" />
                      AI Researched: {summary.aiResearched}
                    </Badge>
                  )}
                  {summary.manual > 0 && (
                    <Badge variant="outline" className="text-xs">
                      <User className="h-3 w-3 mr-1 text-orange-500" />
                      Manual: {summary.manual}
                    </Badge>
                  )}
                </div>
              )}

              {/* Grouped attributes */}
              <AttributeGroup
                origin="feed"
                attributes={groupedAttributes.feed || []}
                defaultOpen={false}
              />
              <AttributeGroup
                origin="ai_extracted"
                attributes={groupedAttributes.ai_extracted || []}
                defaultOpen={false}
              />
              <AttributeGroup
                origin="ai_researched"
                attributes={groupedAttributes.ai_researched || []}
                defaultOpen={true}
              />
              <AttributeGroup
                origin="manual"
                attributes={groupedAttributes.manual || []}
                defaultOpen={true}
              />
              <AttributeGroup
                origin="unknown"
                attributes={groupedAttributes.unknown || []}
                defaultOpen={false}
              />
            </>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

export default AttributeSourceSummary;
